import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { getLiveCollection } from '../../utils/liveData';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { ROUTES, APP_NAME, APP_VERSION, FIREBASE_CONFIG, ORDER_STATUS, KESIF_REMINDER_START_DATE } from '../../constants';
import { getOrderQuantity } from '../../utils';
import { exportData, importData, validateBackupFile } from '../../utils/backupUtils';
import CustomerList from '../customers/CustomerList';
import ProductList from '../productList/ProductList';
import Expenses from '../expenses/Expenses';
import Todos from '../todos/Todos';
import KesifList from '../kesif/KesifList';
import HomeOverview from './HomeOverview';
import AppNavigation from './AppNavigation';
import {
  HomeIcon, UsersIcon, CalendarCheckIcon, TagIcon, WalletIcon, ShieldUserIcon, ClipboardCheckIcon,
  BuildingIcon, KeyIcon, ChevronDownIcon, PlusIcon, PencilIcon, TrashIcon, InfoIcon
} from '../common/Icons';
import './SettingsPanels.css';
// import AccountingReport from '../accounting/AccountingReport'; // GEÇİCİ: Hesaplamalar sekmesi devre dışı

import './Dashboard.css';
import '../../styles/ColorAccents.css'; // Renkli tema vurguları (en son yüklenir)

const parseAnyDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Müşterinin tarihli ödemeleri: ödeme geçmişi + geçmişte tarihsiz kaydedilmiş kısım
// (eski "Ödeme Tamamlandı" işaretlemeleri gibi). Tarihsiz kısım sipariş tarihine yazılır.
const getDatedPayments = (customer) => {
  const history = Array.isArray(customer.paymentHistory) ? customer.paymentHistory : [];
  const payments = history
    .map(p => ({ amount: parseFloat(p.amount) || 0, date: parseAnyDate(p.date) }))
    .filter(p => p.amount > 0 && p.date);
  const historyTotal = payments.reduce((sum, p) => sum + p.amount, 0);
  const undated = (parseFloat(customer.paidAmount) || 0) - historyTotal;
  if (undated > 0.009) {
    const orderDate = parseAnyDate(customer.orderDate);
    if (orderDate) payments.push({ amount: undated, date: orderDate });
  }
  return payments;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userRole, permissions, isAdmin, isMontajci, isYazilimci, isKesifci, loading } = useAuth();

  // LocalStorage'dan son sekmeyi oku
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'dashboard';
  });

  // Rol değiştiğinde veya yüklendiğinde erişim kontrolü
  useEffect(() => {
    if (permissions) {
      // Keşifçi sadece Keşif Listesi'ni görür, girişte direkt oraya düşer
      if (isKesifci) {
        if (activeTab !== 'kesif') setActiveTab('kesif');
        return;
      }
      // Satışcı dashboard'a erişemez, customers'a yönlendir
      if (!permissions.canAccessDashboard && activeTab === 'dashboard') {
        setActiveTab('customers');
      }
      // Montajcı sadece customers ve keşif'e erişebilir
      if (isMontajci && activeTab !== 'customers' && activeTab !== 'kesif') {
        setActiveTab('customers');
      }
      // Kullanıcı yönetimine erişim yoksa ve users sekmesindeyse
      if (!isAdmin && activeTab === 'users') {
        setActiveTab('customers');
      }
    }
  }, [permissions, activeTab, isMontajci, isAdmin, isKesifci]);
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
    total: 0,
    remainingTotal: 0, // Kalan ödemeler toplamı
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    todayCost: 0,
    thisWeekCost: 0,
    thisMonthCost: 0,
    thisYearCost: 0,
    todayNet: 0,
    thisWeekNet: 0,
    thisMonthNet: 0,
    thisYearNet: 0,
    // Ödeme tipine göre günlük gelirler
    todayCash: 0, // Bugün Elden Nakit
    todayIban: 0, // Bugün IBAN
    yesterdayCreditCard: 0 // Dün Kredi Kartı (bir gün gecikme)
  });
  const [lastYearStats, setLastYearStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [monthlyOrderTrend, setMonthlyOrderTrend] = useState([]);
  const [monthlyCustomerTrend, setMonthlyCustomerTrend] = useState([]);
  const [monthlyEarningsTrend, setMonthlyEarningsTrend] = useState([]);
  const [monthlyNetEarningsTrend, setMonthlyNetEarningsTrend] = useState([]);
  const [homeExtras, setHomeExtras] = useState(null); // Anasayfa için ek veriler (karşılaştırma, akış, ajanda)
  const [customerIntent, setCustomerIntent] = useState(null); // 'add': Müşteri listesi ekleme formu açık gelsin
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);

  //Firma bilgileri state'leri
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: ''
  });
  const [companyError, setCompanyError] = useState(null);
  const [companySuccess, setCompanySuccess] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(null); // Silinecek firma bilgisi

  //Şifre değiştirme state'leri
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);

  // Kullanıcı Yönetimi State'leri
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: ROLES.SATISCI
  });
  const [userFormError, setUserFormError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [deleteUserModal, setDeleteUserModal] = useState(null);

  // Notification Modal State
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    details: null
  });

  // Notification Modal göster
  const showNotificationModal = (type, title, message, details = null) => {
    setNotificationModal({
      show: true,
      type,
      title,
      message,
      details
    });
  };

  // Notification Modal kapat
  const closeNotificationModal = () => {
    setNotificationModal({
      show: false,
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  };

  // Tüm istatistikleri çek
  const fetchAllStats = useCallback(async () => {
    try {
      setIsLoadingStats(true);

      // Müşterileri çek
      const customersSnapshot = await getLiveCollection('customers');
      let customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi oluşturduğu müşterileri filtrele
      if (isYazilimci && user?.uid) {
        customers = customers.filter(c => c.createdBy === user.uid);
      }

      // Siparişleri çek
      const ordersSnapshot = await getLiveCollection('orders');
      let orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Giderleri çek
      const expensesSnapshot = await getLiveCollection('expenses');
      // Sadece ödenmiş giderler (Mağaza + Atölye + Montaj) maliyet ve net kazanca dahil edilir; "Bekliyor" olanlar hariç.
      // Firma Giderleri yalnızca firma carisidir: firmadan alınan ürünlerin bedeli sipariş maliyetinde zaten düşüldüğü için eklenmez.
      let expensesList = expensesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(exp => exp.status !== 'pending' && exp.type !== 'firma');

      // Yazılımcı rolü için sadece kendi müşterilerinin siparişlerini filtrele
      if (isYazilimci && user?.uid) {
        const customerIds = customers.map(c => c.id);
        orders = orders.filter(o => customerIds.includes(o.customerId));
      }

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastYear = now.getFullYear() - 1;
      const lastYearYearStart = new Date(lastYear, 0, 1);
      const lastYearYearEnd = new Date(lastYear, 11, 31, 23, 59, 59, 999);
      const lastYearTodayStart = new Date(lastYear, now.getMonth(), now.getDate());
      const lastYearTodayEnd = new Date(lastYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearWeekStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() - 6);
      const lastYearWeekEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthStart = new Date(lastYear, now.getMonth(), 1);
      const lastYearMonthEnd = new Date(lastYear, now.getMonth() + 1, 0, 23, 59, 59, 999);

      // Müşteri istatistikleri
      const customerStats = {
        total: customers.length,
        active: customers.filter(customer => {
          // Sipariş durumu "Yeni Sipariş" olan müşteriler
          return customer.orderStatus === 'new_order';
        }).length,
        today: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bugün (aynı gün kontrolü)
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const customerDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
          return customerDate.getTime() === today.getTime();
        }).length,
        thisWeek: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Son 7 gün (bugün dahil)
          const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);
          return orderDate >= sevenDaysAgo;
        }).length,
        thisMonth: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu ay sipariş veren müşteriler
          return orderDate >= thisMonth;
        }).length,
        thisYear: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu yıl sipariş veren müşteriler (yılın ilk günü)
          const thisYearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= thisYearStart;
        }).length
      };

      // Gelir istatistikleri
      const revenueStats = {
        expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
        total: 0,
        totalCost: 0, // Tüm siparişlerin maliyet toplamı
        netTotal: 0, // Toplam Gelir (Tahsilat) - Toplam Maliyet
        expectedNet: 0, // Toplam Sipariş Tutarı - Toplam Maliyet
        remainingTotal: 0, // Kalan ödemeler toplamı (Atölye + Firma + Montaj + Teslim Edildi)
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        todayCost: 0,
        thisWeekCost: 0,
        thisMonthCost: 0,
        thisYearCost: 0,
        todayExpected: 0,
        thisWeekExpected: 0,
        thisMonthExpected: 0,
        thisYearExpected: 0,
        todayNet: 0,
        thisWeekNet: 0,
        thisMonthNet: 0,
        thisYearNet: 0,
        // Ödeme tipine göre günlük gelirler
        todayCash: 0,
        todayIban: 0,
        yesterdayCreditCard: 0
      };

      const lastYearStatsTemp = {
        total: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0
      };

      // Dönemsel gelir ödeme tarihine, sipariş maliyeti sipariş tarihine yazılır
      const paymentEvents = []; // { date, amount }
      const orderCostEvents = []; // { date, cost }

      // Her müşteri için ödenen tutarları kontrol et (gerçek kazanç)
      customers.forEach(customer => {
        const isNewOrder = customer.orderStatus === ORDER_STATUS.NEW_ORDER;

        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);
        let customerTotalCost = 0;
        let customerTotalRevenue = 0;

        // Toplam Gelir Beklentisi - Tüm siparişler için beklenen gelir
        // Müşterinin siparişlerini bul
        const customerOrders = orders.filter(order => order.customerId === customer.id);

        if (customerOrders.length > 0) {
          // Toplam tutar
          const totalAmount = customerOrders.reduce((sum, order) => {
            return sum + parseFloat(order.totalPrice || 0);
          }, 0);

          // İndirimli tutar
          const discountedTotalAmount = customerOrders.reduce((sum, order) => {
            if (order.discountedPrice && parseFloat(order.discount) > 0) {
              return sum + parseFloat(order.discountedPrice || 0);
            } else {
              return sum + parseFloat(order.totalPrice || 0);
            }
          }, 0);

          // Ahşap Jaluzi toplamı
          const ahsapJaluziTotal = customerOrders.reduce((sum, order) => {
            if (order.productType === 'ahsapJaluzi') {
              return sum + parseFloat(order.totalPrice || 0);
            }
            return sum;
          }, 0);

          // Beklenen gelir hesaplama (Hiyerarşi: Manuel > Vade > Genel İskonto > Tekil İskonto > Ara Toplam)
          const vadeliYuzde = parseFloat(customer.vadeliYuzde) || 0;
          customerTotalRevenue = 0;

          if (customer.manualGrandTotal !== undefined && customer.manualGrandTotal !== null && customer.manualGrandTotal !== '') {
            // 1. Manuel Genel Toplam
            customerTotalRevenue = parseFloat(customer.manualGrandTotal);
          } else if (vadeliYuzde > 0) {
            // 2. Vadeli Hesaplama
            const vadeliTutar = totalAmount - ahsapJaluziTotal;
            const nakitOdeme = ahsapJaluziTotal;
            customerTotalRevenue = (vadeliTutar + (vadeliTutar * vadeliYuzde / 100)) + nakitOdeme;
          } else if (customer.genelIskontoYuzde && parseFloat(customer.genelIskontoYuzde) > 0) {
            // 3. Genel İskonto
            const gIskonto = parseFloat(customer.genelIskontoYuzde);
            customerTotalRevenue = totalAmount - (totalAmount * gIskonto / 100);
          } else if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) {
            // 4. İndirimli tutar varsa
            customerTotalRevenue = discountedTotalAmount;
          } else {
            // 5. Sadece toplam tutar
            customerTotalRevenue = totalAmount;
          }

          revenueStats.expectedTotal += customerTotalRevenue;

          // Tüm siparişlerin maliyetini hesapla (Potansiyel Net Kazanç için)
          customerTotalCost = customerOrders.reduce((sum, order) => {
            return sum + parseFloat(order.totalCost || 0);
          }, 0);

          // Sadece onaylı siparişlerin maliyetini toplam maliyete ekle (Net Gelir için)
          if (!isNewOrder) {
            revenueStats.totalCost += customerTotalCost;
          }

          // Beklenen Net Kazanç (Satış - Maliyet) - Yeni siparişler dahil, maliyetli hesaplanır
          revenueStats.expectedNet += (customerTotalRevenue - customerTotalCost);
        }

        // Toplam Gelir - Ödenen tutarlar (gerçek kazanç) - Yeni siparişler dahil edilmez
        if (!isNewOrder) {
          revenueStats.total += paidAmount;
        }

        // Kalan Ödemeler - Sadece Atölye + Firma + Montaj + Teslim Edildi durumundaki müşterilerin kalan ödemeleri
        if (['atolye', 'firma', 'montaj', 'delivered'].includes(customer.orderStatus)) {
          const remainingAmount = parseFloat(customer.remainingAmount || 0);
          revenueStats.remainingTotal += remainingAmount;
        }

        // Gelir ödemenin alındığı tarihe yazılır. Maliyet ise sipariş firmaya/atölyeye geçtiğinde
        // (Yeni Sipariş dışındaki durumlar) tamamıyla sipariş tarihine yazılır.
        if (!isNewOrder) {
          getDatedPayments(customer).forEach(({ amount, date }) => {
            paymentEvents.push({ date, amount });
          });
          const orderDateForCost = parseAnyDate(customer.orderDate);
          if (orderDateForCost && customerTotalCost) {
            orderCostEvents.push({ date: orderDateForCost, cost: customerTotalCost });
          }
        }

        // Ödeme geçmişinden ödeme tipine göre günlük gelirleri hesapla (sipariş tarihinden bağımsız)
        const paymentHistory = customer.paymentHistory || [];
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

        paymentHistory.forEach(payment => {
          // Firestore Timestamp veya ISO string olabilir
          let paymentDate;
          if (payment.date?.toDate) {
            paymentDate = payment.date.toDate();
          } else if (payment.date?.seconds) {
            paymentDate = new Date(payment.date.seconds * 1000);
          } else {
            paymentDate = new Date(payment.date);
          }
          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate());

          // Bugün Elden Nakit
          if (payment.type === 'cash' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayCash += payment.amount;
          }

          // Bugün IBAN
          if (payment.type === 'iban' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayIban += payment.amount;
          }

          // Dün Kredi Kartı (kredi kartı ödemeleri bir gün gecikmeyle yansır)
          if (payment.type === 'credit_card' && paymentDateOnly.getTime() === yesterday.getTime()) {
            revenueStats.yesterdayCreditCard += payment.amount;
          }
        });

        // Sipariş tarihi yoksa zaman bazlı istatistiklere dahil etme
        if (!customer.orderDate) return;

        // Müşterinin sipariş tarihini al
        let customerDate;
        if (customer.orderDate?.toDate) {
          customerDate = customer.orderDate.toDate();
        } else {
          customerDate = new Date(customer.orderDate);
        }

        // Beklenen gelir sipariş tarihine göre (dönemsel gelir/kâr aşağıda ödeme tarihine göre)
        // Bugün
        const customerDateOnly = new Date(customerDate.getFullYear(), customerDate.getMonth(), customerDate.getDate());
        if (customerDateOnly.getTime() === today.getTime()) {
          revenueStats.todayExpected += customerTotalRevenue;
        }

        // Bu hafta (son 7 gün, bugün dahil)
        const sevenDaysAgoRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoRevenue.setHours(0, 0, 0, 0);
        if (customerDate >= sevenDaysAgoRevenue) {
          revenueStats.thisWeekExpected += customerTotalRevenue;
        }

        // Bu ay
        if (customerDate >= thisMonth) {
          revenueStats.thisMonthExpected += customerTotalRevenue;
        }

        // Bu yıl
        const thisYearStartForNet = new Date(now.getFullYear(), 0, 1);
        if (customerDate >= thisYearStartForNet) {
          revenueStats.thisYearExpected += customerTotalRevenue;
        }

        // Geçen yıl karşılaştırmaları
        if (customerDate >= lastYearYearStart && customerDate <= lastYearYearEnd) {
          lastYearStatsTemp.total += 1;
        }

        if (customerDate >= lastYearTodayStart && customerDate <= lastYearTodayEnd) {
          lastYearStatsTemp.today += 1;
        }

        if (customerDate >= lastYearWeekStart && customerDate <= lastYearWeekEnd) {
          lastYearStatsTemp.thisWeek += 1;
        }

        if (customerDate >= lastYearMonthStart && customerDate <= lastYearMonthEnd) {
          lastYearStatsTemp.thisMonth += 1;
        }
      });

      // Dönemsel gelir (ödeme tarihine göre) ve sipariş maliyeti (sipariş tarihine göre)
      const todayStartRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStartRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      const yearStartRevenue = new Date(now.getFullYear(), 0, 1);
      const addToPeriods = (date, revenueKey, value) => {
        if (date >= todayStartRevenue) revenueStats[`today${revenueKey}`] += value;
        if (date >= weekStartRevenue) revenueStats[`thisWeek${revenueKey}`] += value;
        if (date >= thisMonth) revenueStats[`thisMonth${revenueKey}`] += value;
        if (date >= yearStartRevenue) revenueStats[`thisYear${revenueKey}`] += value;
      };
      paymentEvents.forEach(({ date, amount }) => addToPeriods(date, '', amount));
      orderCostEvents.forEach(({ date, cost }) => addToPeriods(date, 'Cost', cost));

      // Mağaza/Atölye Giderlerini (expenses) maliyet ve net kazanç hesaplamalarına dahil et
      const todayDateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysAgoExp = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      sevenDaysAgoExp.setHours(0, 0, 0, 0);

      expensesList.forEach(exp => {
        const amt = parseFloat(exp.amount) || 0;
        if (amt <= 0) return;

        // Toplam maliyete ekle
        revenueStats.totalCost += amt;

        if (exp.date) {
          let expDate = null;
          if (exp.date?.toDate) {
            expDate = exp.date.toDate();
          } else {
            expDate = new Date(exp.date);
          }

          if (expDate && !isNaN(expDate.getTime())) {
            const expDateOnly = new Date(expDate.getFullYear(), expDate.getMonth(), expDate.getDate());

            // Bugün
            if (expDateOnly.getTime() === todayDateObj.getTime()) {
              revenueStats.todayCost += amt;
            }
            // Bu Hafta
            if (expDate >= sevenDaysAgoExp) {
              revenueStats.thisWeekCost += amt;
            }
            // Bu Ay
            if (expDate >= thisMonth) {
              revenueStats.thisMonthCost += amt;
            }
            // Bu Yıl
            const thisYearStartForExp = new Date(now.getFullYear(), 0, 1);
            if (expDate >= thisYearStartForExp) {
              revenueStats.thisYearCost += amt;
            }
          }
        }
      });

      // Net Kazanç hesaplama (Ödenen - Maliyet)
      revenueStats.netTotal = revenueStats.total - revenueStats.totalCost;
      revenueStats.todayNet = revenueStats.today - revenueStats.todayCost;
      revenueStats.thisWeekNet = revenueStats.thisWeek - revenueStats.thisWeekCost;
      revenueStats.thisMonthNet = revenueStats.thisMonth - revenueStats.thisMonthCost;
      revenueStats.thisYearNet = revenueStats.thisYear - revenueStats.thisYearCost;

      // Aylık sipariş trendi (mevcut yıl)
      const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const currentYear = now.getFullYear();
      const monthlyTrendCounts = Array(12).fill(0);
      const monthlyCustomerCounts = Array(12).fill(0);

      // Sipariş tarihlerini müşteriden okur (orders içinde orderDate yok)
      const customerOrderDateMap = new Map();
      const customerStatusMap = new Map();
      customers.forEach(customer => {
        const dateToUse = customer.orderDate;
        let parsed = null;
        if (dateToUse?.toDate) {
          parsed = dateToUse.toDate();
        } else if (dateToUse) {
          parsed = new Date(dateToUse);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          customerOrderDateMap.set(customer.id, parsed);
        }
        customerStatusMap.set(customer.id, customer.orderStatus);
      });

      orders.forEach(order => {
        // Sipariş Tarihi müşteri kaydından alınır; yoksa ekleme
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (!orderDate) return;
        if (orderDate.getFullYear() === currentYear) {
          monthlyTrendCounts[orderDate.getMonth()] += getOrderQuantity(order);
        }
      });

      customers.forEach(customer => {
        // Müşteri trendi de sipariş tarihine göre; tarih yoksa ekleme
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;

        if (customerDate.getFullYear() === currentYear) {
          monthlyCustomerCounts[customerDate.getMonth()] += 1;
        }
      });

      const monthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyTrendCounts[index]
      }));

      const monthlyCustomerTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyCustomerCounts[index]
      }));

      // Aylık Kazanç Trendi Hesaplama
      const monthlyEarningsCounts = Array(12).fill(0);
      const monthlyNetEarningsCounts = Array(12).fill(0);

      // Aylık kazanç: ödeme tarihine göre; net kazançta sipariş maliyeti sipariş ayından düşülür
      paymentEvents.forEach(({ date, amount }) => {
        if (date.getFullYear() !== currentYear) return;
        monthlyEarningsCounts[date.getMonth()] += amount;
        monthlyNetEarningsCounts[date.getMonth()] += amount;
      });
      orderCostEvents.forEach(({ date, cost }) => {
        if (date.getFullYear() !== currentYear) return;
        monthlyNetEarningsCounts[date.getMonth()] -= cost;
      });

      // Aylık Net Kazanç Grafiğine Giderleri Yansıt (Mevcut Yıl)
      expensesList.forEach(exp => {
        const amt = parseFloat(exp.amount) || 0;
        if (amt <= 0 || !exp.date) return;

        let expDate = null;
        if (exp.date?.toDate) {
          expDate = exp.date.toDate();
        } else {
          expDate = new Date(exp.date);
        }

        if (expDate && !isNaN(expDate.getTime()) && expDate.getFullYear() === currentYear) {
          monthlyNetEarningsCounts[expDate.getMonth()] -= amt;
        }
      });

      const monthlyEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        earnings: monthlyEarningsCounts[index]
      }));

      const monthlyNetEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        netEarnings: monthlyNetEarningsCounts[index]
      }));

      // Anasayfa: geçen yılın aylık serileri (performans grafiğinde karşılaştırma için)
      const prevYear = currentYear - 1;
      const prevEarnings = Array(12).fill(0);
      const prevNetEarnings = Array(12).fill(0);
      const prevOrders = Array(12).fill(0);
      const prevCustomers = Array(12).fill(0);
      paymentEvents.forEach(({ date, amount }) => {
        if (date.getFullYear() !== prevYear) return;
        prevEarnings[date.getMonth()] += amount;
        prevNetEarnings[date.getMonth()] += amount;
      });
      orderCostEvents.forEach(({ date, cost }) => {
        if (date.getFullYear() === prevYear) prevNetEarnings[date.getMonth()] -= cost;
      });
      expensesList.forEach(exp => {
        const amt = parseFloat(exp.amount) || 0;
        const expDate = parseAnyDate(exp.date);
        if (amt > 0 && expDate && expDate.getFullYear() === prevYear) prevNetEarnings[expDate.getMonth()] -= amt;
      });
      orders.forEach(order => {
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (orderDate && orderDate.getFullYear() === prevYear) prevOrders[orderDate.getMonth()] += getOrderQuantity(order);
      });
      customerOrderDateMap.forEach(date => {
        if (date.getFullYear() === prevYear) prevCustomers[date.getMonth()] += 1;
      });

      // Geçen yıl aynı ayın bugüne kadarki tahsilatı ("Bu ay tahsilat" karşılaştırması)
      const lastYearSameDayEnd = new Date(prevYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthToDateRevenue = paymentEvents
        .filter(({ date }) => date >= lastYearMonthStart && date <= lastYearSameDayEnd)
        .reduce((sum, p) => sum + p.amount, 0);

      // Sipariş akışı: teslim edilmemiş müşterilerin durumlara dağılımı
      const pipeline = { [ORDER_STATUS.NEW_ORDER]: 0, [ORDER_STATUS.FIRMA]: 0, [ORDER_STATUS.ATOLYE]: 0, [ORDER_STATUS.MONTAJ]: 0 };
      customers.forEach(c => {
        if (c.orderStatus in pipeline) pipeline[c.orderStatus] += 1;
      });
      const remainingCustomerCount = customers.filter(c =>
        ['atolye', 'firma', 'montaj', 'delivered'].includes(c.orderStatus) && parseFloat(c.remainingAmount) > 0
      ).length;

      // Son 6 ayın yeni müşteri sayıları (yıl geçişini de kapsar)
      const recentCustomerMonths = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
        return { label: monthLabels[d.getMonth()], year: d.getFullYear(), month: d.getMonth(), count: 0 };
      });
      customerOrderDateMap.forEach(date => {
        const slot = recentCustomerMonths.find(m => m.year === date.getFullYear() && m.month === date.getMonth());
        if (slot) slot.count += 1;
      });

      // Keşif ajandası: yapılmamış keşifler (tarih aralığı anasayfada süzülür)
      const kesifAgenda = customers
        .filter(c => typeof c.inspectionDate === 'string' && c.inspectionDate >= KESIF_REMINDER_START_DATE && !c.inspectionDone)
        .map(c => ({
          id: c.id,
          name: c.name,
          phone: c.phone1,
          address: c.address,
          dateKey: c.inspectionDate,
          time: c.inspectionTime || ''
        }));

      // Montaj ajandası: teslimat/montaj tarihi olan, teslim edilmemiş ve montajı yapılmamış müşteriler
      const montajAgenda = customers
        .filter(c =>
          typeof c.deliveryDate === 'string' && c.deliveryDate >= KESIF_REMINDER_START_DATE &&
          c.orderStatus !== ORDER_STATUS.DELIVERED && !c.montajDone
        )
        .map(c => ({
          id: c.id,
          name: c.name,
          phone: c.phone1,
          address: c.address,
          dateKey: c.deliveryDate,
          time: c.deliveryTime || ''
        }));

      setCustomerStats(customerStats);
      setRevenueStats(revenueStats);
      setLastYearStats(lastYearStatsTemp);
      setMonthlyOrderTrend(monthlyTrendData);
      setMonthlyCustomerTrend(monthlyCustomerTrendData);
      setMonthlyEarningsTrend(monthlyEarningsTrendData);
      setMonthlyNetEarningsTrend(monthlyNetEarningsTrendData);
      setHomeExtras({
        prevEarnings,
        prevNetEarnings,
        prevOrders,
        prevCustomers,
        lastYearMonthToDateRevenue,
        pipeline,
        remainingCustomerCount,
        recentCustomerMonths,
        kesifAgenda,
        montajAgenda
      });
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isYazilimci, user?.uid]);

  // İstatistikleri yükle
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAllStats();
    }
  }, [fetchAllStats, activeTab]);

  // Aktif sekme değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
    // "Yeni müşteri" isteği yalnızca o geçişte geçerli
    if (activeTab !== 'customers') setCustomerIntent(null);
  }, [activeTab]);

  const openNewCustomer = () => {
    setCustomerIntent('add');
    setActiveTab('customers');
  };



  // Firma bilgilerini Firebase'den çek
  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoadingCompanies(true);
      const companiesSnapshot = await getLiveCollection('companies');
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // İsme göre sırala
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata:', error);
      setCompanyError('Firma bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  // Firma bilgileri modalı açıldığında verileri çek
  useEffect(() => {
    if (showCompanyInfo) {
      fetchCompanies();
    }
  }, [showCompanyInfo, fetchCompanies]);

  // Firma ekle veya güncelle
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanyError(null);
    setCompanySuccess(false);

    // Validasyonlar
    if (!companyForm.name || !companyForm.phone) {
      setCompanyError('Lütfen firma adı ve telefon numarasını girin.');
      return;
    }

    // Telefon numarası validasyonu (sadece rakam ve + işareti)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(companyForm.phone.replace(/\s/g, ''))) {
      setCompanyError('Geçerli bir telefon numarası girin (örn: 905321234567 veya +905321234567)');
      return;
    }

    try {
      const phoneNumber = companyForm.phone.replace(/\s/g, ''); // Boşlukları temizle

      if (editingCompany) {
        // Güncelleme
        await updateDoc(doc(db, 'companies', editingCompany.id), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          updatedAt: new Date()
        });
        setCompanySuccess('Firma bilgileri başarıyla güncellendi!');
      } else {
        // Yeni ekleme
        // Aynı isimde firma var mı kontrol et
        const existingCompany = companies.find(c =>
          c.name.toLowerCase().trim() === companyForm.name.toLowerCase().trim()
        );
        if (existingCompany) {
          setCompanyError('Bu isimde bir firma zaten mevcut.');
          return;
        }

        await addDoc(collection(db, 'companies'), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: user?.uid || null
        });
        setCompanySuccess('Firma başarıyla eklendi!');
      }

      // Formu temizle
      setCompanyForm({ name: '', phone: '' });
      setEditingCompany(null);

      // Listeyi yeniden yükle
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma kaydedilirken hata:', error);
      let errorMessage = 'Firma kaydedilirken bir hata oluştu.';
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        errorMessage = 'Firma ekleme yetkiniz bulunmamaktadır. Lütfen yöneticinizle iletişime geçin.';
      } else if (error.message) {
        errorMessage = `Firma kaydedilirken bir hata oluştu: ${error.message}`;
      }
      setCompanyError(errorMessage);
    }
  };

  // Firma düzenle
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      phone: company.phone || ''
    });
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // Firma silme modal'ını aç
  const handleDeleteCompanyClick = (company) => {
    setDeleteCompanyModal(company);
  };

  // Firma silme modal'ını kapat
  const handleCloseDeleteCompanyModal = () => {
    setDeleteCompanyModal(null);
  };

  // Firma sil
  const handleDeleteCompany = async () => {
    if (!deleteCompanyModal) return;

    try {
      await deleteDoc(doc(db, 'companies', deleteCompanyModal.id));
      setCompanySuccess('Firma başarıyla silindi!');
      setDeleteCompanyModal(null);
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma silinirken hata:', error);
      setCompanyError('Firma silinirken bir hata oluştu: ' + error.message);
      setDeleteCompanyModal(null);
    }
  };

  // Firma formunu iptal et
  const handleCancelCompany = () => {
    setCompanyForm({ name: '', phone: '' });
    setEditingCompany(null);
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI ========

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingUsers(true);
    try {
      const usersSnapshot = await getLiveCollection('users');
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(user => !user.disabled); // Disabled kullanıcıları filtrele
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin]);

  // Kullanıcıları yükle (admin ise)
  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, fetchUsers]);

  // Yeni kullanıcı oluştur
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormError(null);

    if (!newUserForm.email || !newUserForm.password || !newUserForm.displayName) {
      setUserFormError('Tüm alanları doldurun');
      return;
    }

    if (newUserForm.password.length < 6) {
      setUserFormError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      // Firebase Auth'da kullanıcı oluştur - Secondary auth instance kullanıyoruz
      // Ana kullanıcının oturumunu kapatmadan yeni kullanıcı oluşturmak için
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword: createUser } = await import('firebase/auth');

      // Mevcut Secondary app varsa sil
      const apps = getApps();
      const existingSecondary = apps.find(app => app.name === 'Secondary');
      if (existingSecondary) {
        await deleteApp(existingSecondary);
      }

      // Geçici bir Firebase app instance oluştur (constants'dan config al)
      const secondaryApp = initializeApp(FIREBASE_CONFIG, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUser(secondaryAuth, newUserForm.email, newUserForm.password);
        const newUser = userCredential.user;

        // Firestore'da kullanıcı kaydı oluştur
        await setDoc(doc(db, 'users', newUser.uid), {
          email: newUserForm.email,
          displayName: newUserForm.displayName,
          role: newUserForm.role,
          createdAt: new Date(),
          createdBy: user.uid
        });

        // Secondary app'i temizle
        await deleteApp(secondaryApp);

        // Formu sıfırla ve modalı kapat
        setNewUserForm({
          email: '',
          password: '',
          displayName: '',
          role: ROLES.SATISCI
        });
        setShowAddUserModal(false);

        // Kullanıcı listesini yenile
        fetchUsers();

        showNotificationModal('success', 'Başarılı', `${newUserForm.displayName} kullanıcısı başarıyla oluşturuldu.`);
      } catch (error) {
        await deleteApp(secondaryApp);
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturulurken hata:', error);
      if (error.code === 'auth/email-already-in-use') {
        setUserFormError('Bu e-posta adresi zaten kullanılıyor');
      } else if (error.code === 'auth/invalid-email') {
        setUserFormError('Geçersiz e-posta adresi');
      } else if (error.code === 'auth/weak-password') {
        setUserFormError('Şifre çok zayıf');
      } else {
        setUserFormError('Kullanıcı oluşturulurken bir hata oluştu');
      }
    }
  };

  // Kullanıcı rolünü güncelle
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });

      // Local state güncelle
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUserRole(null);

      showNotificationModal('success', 'Başarılı', 'Kullanıcı rolü güncellendi');
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      showNotificationModal('error', 'Hata', 'Rol güncellenirken bir hata oluştu');
    }
  };

  // Kullanıcı sil - Kullanıcıyı disabled olarak işaretle (Firestore'dan silme, sadece devre dışı bırak)
  const handleDeleteUser = async (userId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        showNotificationModal('error', 'Hata', 'Kullanıcı bulunamadı');
        return;
      }

      // Firebase Auth'dan kullanıcıyı silmek için Cloud Functions endpoint'ini dene
      try {
        const functions = await import('firebase/functions');
        const { getFunctions, httpsCallable } = functions;
        const funcs = getFunctions();
        const deleteUserFunction = httpsCallable(funcs, 'deleteUser');

        // Cloud Functions ile Firebase Auth'dan da sil
        await deleteUserFunction({ userId: userId, email: userToDelete.email });

        // Başarılı olursa Firestore'dan da sil
        await deleteDoc(doc(db, 'users', userId));
      } catch (functionsError) {
        // Cloud Functions yoksa veya hata varsa, sadece disabled olarak işaretle
        // Firestore'dan SİLME, sadece disabled yap
        await updateDoc(doc(db, 'users', userId), {
          disabled: true,
          disabledAt: new Date(),
          disabledBy: user.uid
        });
      }

      // Kullanıcı listesini yeniden yükle
      await fetchUsers();

      setDeleteUserModal(null);
      showNotificationModal('success', 'Başarılı', 'Kullanıcı başarıyla devre dışı bırakıldı');
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error);
      showNotificationModal('error', 'Hata', 'Kullanıcı silinirken bir hata oluştu: ' + error.message);
    }
  };

  // Rol adını Türkçe olarak döndür
  const getRoleName = (role) => {
    const roleNames = {
      [ROLES.ADMIN]: 'Admin',
      [ROLES.SATISCI]: 'Satışcı',
      [ROLES.MONTAJCI]: 'Montajcı',
      [ROLES.YAZILIMCI]: 'Yazılımcı',
      [ROLES.KESIFCI]: 'Keşifçi'
    };
    return roleNames[role] || role;
  };

  // Rol badge rengini döndür
  const getRoleBadgeClass = (role) => {
    const classes = {
      [ROLES.ADMIN]: 'role-badge-admin',
      [ROLES.SATISCI]: 'role-badge-satisci',
      [ROLES.MONTAJCI]: 'role-badge-montajci',
      [ROLES.YAZILIMCI]: 'role-badge-yazilimci',
      [ROLES.KESIFCI]: 'role-badge-kesifci'
    };
    return classes[role] || '';
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI SONU ========

  const handleLogout = async () => {
    try {
      // Firebase'den çıkış yap
      await signOut(auth);
      // Firebase otomatik olarak oturumu temizler
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      // Hata olsa bile ana sayfaya yönlendir
      navigate(ROUTES.LOGIN);
    }
  };


  // Backup fonksiyonu
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await exportData(userRole, user?.uid);
      showNotificationModal(
        'success',
        'Yedekleme Başarılı',
        'Verileriniz başarıyla yedeklendi.',
        {
          customers: result.customers,
          orders: result.orders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yedekleme Hatası', error.message);
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import modal'ını aç
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
  };

  // Dosya seçimi
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateBackupFile(file);
        setImportFile(file);
      } catch (error) {
        showNotificationModal('error', 'Dosya Hatası', error.message);
        event.target.value = '';
      }
    }
  };

  // Import işlemi
  const handleImport = async () => {
    if (!importFile) {
      showNotificationModal('warning', 'Dosya Seçilmedi', 'Lütfen bir yedek dosyası seçin.');
      return;
    }

    try {
      setIsImporting(true);
      const result = await importData(importFile);

      // Modal'ı kapat
      setShowImportModal(false);
      setImportFile(null);

      // İstatistikleri yeniden yükle
      fetchAllStats();

      // Başarı modal'ını göster
      showNotificationModal(
        'success',
        'Yükleme Başarılı',
        'Yedek dosyası başarıyla yüklendi.',
        {
          backupCustomers: result.totalCustomers,
          backupOrders: result.totalOrders,
          addedCustomers: result.addedCustomers,
          updatedCustomers: result.updatedCustomers,
          addedOrders: result.addedOrders,
          updatedOrders: result.updatedOrders
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yükleme Hatası', error.message);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Modal'ı kapat
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  //Şifre değiştirme fonksiyonu
  const handleChangePasswordRequest = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validasyonlar
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifre ve şifre onayı eşleşmiyor.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('Yeni şifre eski şifre ile aynı olamaz.');
      return;
    }

    setShowPasswordConfirmModal(true);
  };

  const confirmPasswordChange = async () => {
    setShowPasswordConfirmModal(false);
    try {
      setIsChangingPassword(true);

      //Mevcut şifreyi doğrula
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError("Kullanıcı bilgisi bulunamadı.");
        setIsChangingPassword(false);
        return;
      }

      // Mevcut şifreyi doğrula (ayrı try-catch ile)
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (authError) {
        console.error('Şifre doğrulama hatası:', authError);

        // Mevcut şifre ile ilgili hatalar
        if (authError.code === 'auth/wrong-password' ||
          authError.code === 'auth/invalid-credential' ||
          authError.code === 'auth/invalid-email' ||
          authError.message?.includes('password') ||
          authError.message?.includes('credential')) {
          setPasswordError('Mevcut şifre hatalı. Lütfen doğru şifreyi girin.');
        } else if (authError.code === 'auth/user-mismatch') {
          setPasswordError('Kullanıcı bilgisi eşleşmiyor. Lütfen tekrar giriş yapın.');
        } else if (authError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Mevcut şifre doğrulanamadı. Lütfen şifrenizi kontrol edin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Şifreyi güncelle (ayrı try-catch ile)
      try {
        await updatePassword(user, newPassword);
      } catch (updateError) {
        console.error('Şifre güncelleme hatası:', updateError);

        if (updateError.code === 'auth/weak-password') {
          setPasswordError('Yeni şifre çok zayıf. Daha güçlü bir şifre seçin (en az 6 karakter, büyük/küçük harf, rakam önerilir).');
        } else if (updateError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Başarılı
      setPasswordSuccess('Şifreniz başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setPasswordError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Kullanıcı Yönetimi Render
  const renderUserManagement = () => {
    return (
      <div className="user-management-content">
        <div className="section-header">
          <h2><i className="fas fa-users-cog"></i> Kullanıcı Yönetimi</h2>
          <button
            className="btn-add-user"
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            Yeni Kullanıcı
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Kullanıcılar yükleniyor...</span>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Kullanıcı Adı</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Oluşturulma Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || '-'}</td>
                    <td>{u.email}</td>
                    <td>
                      {editingUserRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                          onBlur={() => setEditingUserRole(null)}
                          autoFocus
                        >
                          <option value={ROLES.ADMIN}>Admin</option>
                          <option value={ROLES.SATISCI}>Satışcı</option>
                          <option value={ROLES.MONTAJCI}>Montajcı</option>
                          <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                          <option value={ROLES.KESIFCI}>Keşifçi</option>
                        </select>
                      ) : (
                        <span
                          className={`role-badge ${getRoleBadgeClass(u.role)}`}
                          onClick={() => u.id !== user?.uid && setEditingUserRole(u.id)}
                          style={{ cursor: u.id !== user?.uid ? 'pointer' : 'default' }}
                          title={u.id !== user?.uid ? 'Rolü değiştirmek için tıklayın' : 'Kendi rolünüzü değiştiremezsiniz'}
                        >
                          {getRoleName(u.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.createdAt?.toDate?.()
                        ? new Date(u.createdAt.toDate()).toLocaleDateString('tr-TR')
                        : '-'}
                    </td>
                    <td>
                      {u.id !== user?.uid ? (
                        <button
                          className="btn-delete-user"
                          onClick={() => setDeleteUserModal(u)}
                          title="Kullanıcıyı Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      ) : (
                        <span className="current-user-badge">Siz</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      Henüz kullanıcı bulunmamaktadır.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="role-info-section">
          <h3><i className="fas fa-info-circle"></i> Rol Yetkileri</h3>
          <div className="role-info-grid">
            <div className="role-info-card">
              <span className="role-badge role-badge-admin">Admin</span>
              <p>Tüm sisteme tam erişim. Kullanıcı oluşturma ve silme yetkisi.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-satisci">Satışcı</span>
              <p>Müşteri ve sipariş ekleme, düzenleme, silme. Dashboard'a erişim yok.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-montajci">Montajcı</span>
              <p>Sadece montaj durumundaki siparişleri görme ve teslim edildi olarak işaretleme.</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-yazilimci">Yazılımcı</span>
              <p>Tüm özelliklere erişim, ancak sadece kendi oluşturduğu verileri görme.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isKesifci || activeTab === 'kesif') {
      return (
        <KesifList
          user={user}
          onlyOwnData={isYazilimci}
          pageTitle={isKesifci ? 'Keşif Listesi' : 'Keşif & Montaj'}
          reminderTypes={isKesifci ? ['kesif'] : undefined}
          reminderDefaultFilter={isMontajci ? 'montaj' : 'all'}
        />
      );
    }
    switch (activeTab) {
      case 'customers':
        return <CustomerList onCustomerChange={fetchAllStats} userRole={userRole} userId={user?.uid} startAdding={customerIntent === 'add'} />;
      case 'products':
        if (isMontajci) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <ProductList />;
      case 'expenses':
        if (isMontajci) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <Expenses />;
      case 'todos':
        if (isMontajci || isKesifci) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <Todos />;
      case 'users':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return renderUserManagement();
      /* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
      case 'accounting':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <AccountingReport />;
      */
      case 'dashboard':
      default:
        // Satışcı dashboard'a erişemez
        if (!permissions?.canAccessDashboard) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Dashboard'a erişim yetkiniz bulunmamaktadır.</p>
              <p>Müşteri Listesi sekmesinden işlemlerinizi yapabilirsiniz.</p>
            </div>
          );
        }
        return (
          <div className="dashboard-content">
            <HomeOverview
              user={user}
              isLoading={isLoadingStats}
              canCreateCustomer={!!permissions?.canCreateCustomer}
              canManageExpenses={!isMontajci && !isKesifci}
              customerStats={customerStats}
              revenueStats={revenueStats}
              lastYearStats={lastYearStats}
              homeExtras={homeExtras}
              monthlyEarningsTrend={monthlyEarningsTrend}
              monthlyNetEarningsTrend={monthlyNetEarningsTrend}
              monthlyOrderTrend={monthlyOrderTrend}
              monthlyCustomerTrend={monthlyCustomerTrend}
              onNavigate={setActiveTab}
              onNewCustomer={openNewCustomer}
              onExport={handleExport}
              onImport={handleImportClick}
              isExporting={isExporting}
              isImporting={isImporting}
            />

            {/* Ayarlar: Firma Bilgileri + Şifre Değiştir */}
            <div className="ho st-wrap">
              <div className="st-grid">
                <section className={`ho-card st-card${showCompanyInfo ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="st-toggle"
                    aria-expanded={showCompanyInfo}
                    onClick={() => {
                      setShowCompanyInfo(!showCompanyInfo);
                      setCompanyError(null);
                      setCompanySuccess(false);
                      if (!showCompanyInfo) {
                        handleCancelCompany();
                      }
                    }}
                  >
                    <span className="st-icon"><BuildingIcon size={19} /></span>
                    <span className="st-head">
                      <span className="ho-card-title">Firma bilgileri</span>
                      <span className="ho-card-sub">WhatsApp sipariş mesajlarının gideceği firmalar</span>
                    </span>
                    <span className="st-chevron"><ChevronDownIcon size={18} /></span>
                  </button>

                  {showCompanyInfo && (
                    <div className="st-body">
                      {companyError && <div className="st-message st-message-error">{companyError}</div>}
                      {companySuccess && <div className="st-message st-message-success">{companySuccess}</div>}

                      <form onSubmit={handleSaveCompany} className="st-form">
                        <div className="st-field">
                          <label htmlFor="companyName">Firma adı</label>
                          <input
                            type="text"
                            id="companyName"
                            value={companyForm.name}
                            onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                            placeholder="Firma adını girin"
                            required
                            disabled={isLoadingCompanies}
                          />
                        </div>
                        <div className="st-field">
                          <label htmlFor="companyPhone">Telefon numarası</label>
                          <input
                            type="tel"
                            id="companyPhone"
                            value={companyForm.phone}
                            onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                            placeholder="905321234567"
                            required
                            disabled={isLoadingCompanies}
                          />
                          <small>Ülke kodu ile birlikte girin (örn: 905321234567)</small>
                        </div>
                        <div className="st-actions">
                          {editingCompany && (
                            <button type="button" className="ho-btn" onClick={handleCancelCompany} disabled={isLoadingCompanies}>
                              İptal
                            </button>
                          )}
                          <button type="submit" className="ho-btn ho-btn-primary" disabled={isLoadingCompanies}>
                            {isLoadingCompanies ? 'Kaydediliyor…' : editingCompany ? 'Güncelle' : (<><PlusIcon size={16} strokeWidth={2.2} /> Firma ekle</>)}
                          </button>
                        </div>
                      </form>

                      <div className="st-list-head">
                        <span>Kayıtlı firmalar</span>
                        <span className="st-count">{companies.length}</span>
                      </div>
                      {isLoadingCompanies && companies.length === 0 ? (
                        <div className="st-empty">Yükleniyor…</div>
                      ) : companies.length === 0 ? (
                        <div className="st-empty">Henüz firma eklenmemiş.</div>
                      ) : (
                        <ul className="st-list">
                          {companies.map((company) => (
                            <li key={company.id} className={editingCompany?.id === company.id ? 'is-editing' : ''}>
                              <span className="st-list-avatar">{(company.name || '?').charAt(0).toLocaleUpperCase('tr-TR')}</span>
                              <span className="st-list-text">
                                <strong>{company.name}</strong>
                                <span>{company.phone}</span>
                              </span>
                              <button
                                type="button"
                                className="ho-icon-btn"
                                onClick={() => handleEditCompany(company)}
                                disabled={isLoadingCompanies}
                                aria-label={`${company.name} düzenle`}
                                title="Düzenle"
                              >
                                <PencilIcon size={15} />
                              </button>
                              <button
                                type="button"
                                className="ho-icon-btn st-danger"
                                onClick={() => handleDeleteCompanyClick(company)}
                                disabled={isLoadingCompanies}
                                aria-label={`${company.name} sil`}
                                title="Sil"
                              >
                                <TrashIcon size={15} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <p className="st-note">
                        <InfoIcon size={14} />
                        <span>Eklenen firmalar WhatsApp mesaj gönderme bölümünde görünür. Firma adı benzersiz olmalıdır.</span>
                      </p>
                    </div>
                  )}
                </section>

                <section className={`ho-card st-card${showPasswordChange ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="st-toggle"
                    aria-expanded={showPasswordChange}
                    onClick={() => {
                      setShowPasswordChange(!showPasswordChange);
                      setPasswordError('');
                      setPasswordSuccess('');
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    <span className="st-icon"><KeyIcon size={19} /></span>
                    <span className="st-head">
                      <span className="ho-card-title">Şifre değiştir</span>
                      <span className="ho-card-sub">Hesabınızın giriş şifresini güncelleyin</span>
                    </span>
                    <span className="st-chevron"><ChevronDownIcon size={18} /></span>
                  </button>

                  {showPasswordChange && (
                    <div className="st-body">
                      {passwordError && <div className="st-message st-message-error">{passwordError}</div>}
                      {passwordSuccess && <div className="st-message st-message-success">{passwordSuccess}</div>}

                      <form onSubmit={handleChangePasswordRequest} className="st-form">
                        <div className="st-field">
                          <label htmlFor="currentPassword">Mevcut şifre</label>
                          <input
                            type="password"
                            id="currentPassword"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Mevcut şifreniz"
                            required
                            disabled={isChangingPassword}
                            autoComplete="current-password"
                          />
                        </div>
                        <div className="st-field">
                          <label htmlFor="newPassword">Yeni şifre</label>
                          <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="En az 6 karakter"
                            required
                            disabled={isChangingPassword}
                            autoComplete="new-password"
                            minLength={6}
                          />
                        </div>
                        <div className="st-field">
                          <label htmlFor="confirmPassword">Yeni şifre (tekrar)</label>
                          <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Yeni şifrenizi tekrar girin"
                            required
                            disabled={isChangingPassword}
                            autoComplete="new-password"
                            minLength={6}
                          />
                        </div>
                        <div className="st-actions">
                          <button
                            type="button"
                            className="ho-btn"
                            onClick={() => {
                              setShowPasswordChange(false);
                              setPasswordError('');
                              setPasswordSuccess('');
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                            }}
                            disabled={isChangingPassword}
                          >
                            İptal
                          </button>
                          <button type="submit" className="ho-btn ho-btn-primary" disabled={isChangingPassword}>
                            {isChangingPassword ? 'Değiştiriliyor…' : 'Şifreyi değiştir'}
                          </button>
                        </div>
                      </form>

                      <p className="st-note">
                        <InfoIcon size={14} />
                        <span>Güçlü bir şifre için büyük/küçük harf, rakam ve özel karakter kullanın. Şifre değişince <strong>diğer cihazlardaki oturumlar kapanır.</strong></span>
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        );
    }
  };

  // Menü öğeleri (rol bazlı). Mobilde ilk 4'ü alt çubukta, fazlası "Daha fazla" altında.
  const navItems = [
    permissions?.canAccessDashboard && { key: 'dashboard', label: 'Anasayfa', icon: HomeIcon },
    !isKesifci && { key: 'customers', label: isMontajci ? 'Montaj Listesi' : 'Müşteriler', shortLabel: isMontajci ? 'Montaj' : 'Müşteriler', icon: UsersIcon },
    { key: 'kesif', label: isKesifci ? 'Keşif Listesi' : 'Keşif & Montaj', shortLabel: isKesifci ? 'Keşif' : 'Planlama', icon: CalendarCheckIcon },
    !isMontajci && !isKesifci && { key: 'products', label: 'Ürünler', icon: TagIcon },
    !isMontajci && !isKesifci && { key: 'expenses', label: 'Giderler', icon: WalletIcon },
    !isMontajci && !isKesifci && { key: 'todos', label: 'Yapılacaklar', shortLabel: 'Görevler', icon: ClipboardCheckIcon },
    isAdmin && { key: 'users', label: 'Kullanıcılar', icon: ShieldUserIcon },
  ].filter(Boolean);

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fcfcfc' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 15px' }}></div>
          <h2 style={{ color: '#555', fontSize: '18px', fontWeight: '500' }}>Yetkiler Kontrol Ediliyor...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-container${navItems.length > 1 ? ' app-has-tabbar' : ''}`}>
      <AppNavigation
        items={navItems}
        activeTab={activeTab}
        onSelect={(key) => {
          if (key === 'customers') setCustomerIntent(null);
          setActiveTab(key);
        }}
        user={user}
        roleName={userRole ? getRoleName(userRole) : ''}
        onLogout={handleLogout}
      />

      <main className="dashboard-main">
        {renderContent()}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="footer-brand-mark" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
                <path d="M2 7h20" />
              </svg>
            </span>
            <span className="footer-brand-name">{APP_NAME}</span>
          </div>

          <a
            href="https://kahramantech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-developer"
          >
            <span className="footer-developer-label">Developed by</span>
            <span className="footer-developer-name">KahramanTech</span>
            <svg className="footer-developer-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 7h10v10" />
              <path d="M7 17 17 7" />
            </svg>
          </a>

          <div className="footer-info">
            <span className="footer-copyright">
              © {new Date().getFullYear()} Tüm hakları saklıdır.
            </span>
            <span className="version-badge">
              <span className="version-dot" aria-hidden="true"></span>
              v{APP_VERSION}
            </span>
          </div>
        </div>
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content import-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-upload"></i>
                Yedek Dosyası Yükle
              </h3>
              <button onClick={closeImportModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                  className="file-input"
                />
                <label htmlFor="backup-file-input" className="file-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>JSON dosyası seçin veya sürükleyin</span>
                </label>

                {importFile && (
                  <div className="selected-file">
                    <i className="fas fa-file-alt"></i>
                    <span>{importFile.name}</span>
                    <small>({(importFile.size / 1024).toFixed(1)} KB)</small>
                  </div>
                )}
              </div>

              <div className="import-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>Dikkat:</strong>
                  <ul>
                    <li>Backup dosyası JSON formatında olmalıdır</li>
                    <li>Mevcut verileriniz korunur, silinmez</li>
                    <li>Aynı ID'ye sahip veriler güncellenir</li>
                    <li>İşlem geri alınamaz, önceden yedek aldığınızdan emin olun</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="btn-primary import-confirm-btn"
              >
                {isImporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    Yükleniyor...
                  </span>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Yedek Yükle
                  </>
                )}
              </button>
              <button
                onClick={closeImportModal}
                disabled={isImporting}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content notification-modal ${notificationModal.type}`}>
            <div className="modal-header">
              <h3>
                {notificationModal.type === 'success' && <i className="fas fa-check-circle"></i>}
                {notificationModal.type === 'error' && <i className="fas fa-times-circle"></i>}
                {notificationModal.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {notificationModal.type === 'info' && <i className="fas fa-info-circle"></i>}
                {notificationModal.title}
              </h3>
              <button onClick={closeNotificationModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>

              {/* Export detayları */}
              {notificationModal.details && notificationModal.details.customers !== undefined && !notificationModal.details.backupCustomers && (
                <div className="notification-details">
                  <div className="detail-item">
                    <i className="fas fa-users"></i>
                    <span>{notificationModal.details.customers} müşteri</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-shopping-cart"></i>
                    <span>{notificationModal.details.orders} sipariş</span>
                  </div>
                </div>
              )}

              {/* Import detayları */}
              {notificationModal.details && notificationModal.details.backupCustomers !== undefined && (
                <div className="notification-details import-details">
                  <div className="detail-section">
                    <h4>📊 Yedek Dosyası</h4>
                    <div className="detail-row">
                      <span>{notificationModal.details.backupCustomers} müşteri</span>
                      <span>{notificationModal.details.backupOrders} sipariş</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>✅ İşlem Sonucu</h4>
                    <div className="detail-grid">
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Müşteri</span>
                        <span className="detail-value">{notificationModal.details.addedCustomers}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Müşteri</span>
                        <span className="detail-value">{notificationModal.details.updatedCustomers}</span>
                      </div>
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Sipariş</span>
                        <span className="detail-value">{notificationModal.details.addedOrders}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Sipariş</span>
                        <span className="detail-value">{notificationModal.details.updatedOrders}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeNotificationModal} className="btn-primary">
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Silme Onay Modal */}
      {deleteCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-company-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Firma Sil
              </h3>
              <button onClick={handleCloseDeleteCompanyModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteCompanyModal.name}</strong> adlı firmayı silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz!
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleDeleteCompany}
                className="btn-primary delete-confirm-btn"
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? (
                  <>
                    <div className="btn-spinner"></div>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Evet, Sil
                  </>
                )}
              </button>
              <button
                onClick={handleCloseDeleteCompanyModal}
                className="btn-secondary"
                disabled={isLoadingCompanies}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kullanıcı Ekleme Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content add-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-plus"></i>
                Yeni Kullanıcı Oluştur
              </h3>
              <button onClick={() => {
                setShowAddUserModal(false);
                setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                setUserFormError(null);
              }} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {userFormError && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {userFormError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="displayName">
                    <i className="fas fa-user"></i>
                    Kullanıcı Adı
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={newUserForm.displayName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Örn: Ahmet Yılmaz"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    E-posta
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    Şifre
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="En az 6 karakter"
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">
                    <i className="fas fa-user-tag"></i>
                    Rol
                  </label>
                  <select
                    id="role"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value={ROLES.SATISCI}>Satışcı</option>
                    <option value={ROLES.MONTAJCI}>Montajcı</option>
                    <option value={ROLES.YAZILIMCI}>Yazılımcı</option>
                    <option value={ROLES.KESIFCI}>Keşifçi</option>
                    <option value={ROLES.ADMIN}>Admin</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-user-plus"></i>
                  Kullanıcı Oluştur
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                    setUserFormError(null);
                  }}
                  className="btn-secondary"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {deleteUserModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                Kullanıcı Sil
              </h3>
              <button onClick={() => setDeleteUserModal(null)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <strong>{deleteUserModal.displayName || deleteUserModal.email}</strong> kullanıcısını silmek istediğinizden emin misiniz?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                Bu işlem geri alınamaz! Kullanıcı sisteme giriş yapamayacak. Bu Gmail bilgisi ile tekrar bir kullanıcı oluşturamayacaksınız, kullanıcı tekrar bu bilgilerle sisteme erişemeyecek.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleDeleteUser(deleteUserModal.id)}
                className="btn-primary delete-confirm-btn"
              >
                <i className="fas fa-trash"></i>
                Evet, Sil
              </button>
              <button
                onClick={() => setDeleteUserModal(null)}
                className="btn-secondary"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h2>Şifre Değiştirme Onayı</h2>
              <button
                onClick={() => setShowPasswordConfirmModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '18px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  padding: 0,
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }}
                title="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p style={{ marginTop: '16px', fontSize: '15px', textAlign: 'center' }}>
                Şifrenizi değiştirmek üzeresiniz. Bu işlem gerçekleştikten sonra güvenlik nedeniyle <strong>diğer tüm cihazlardaki açık oturumlarınız otomatik olarak kapatılacaktır</strong>.
              </p>
              <p style={{ marginTop: '8px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                İşleme devam etmek istediğinize emin misiniz?
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn-cancel"
                onClick={() => setShowPasswordConfirmModal(false)}
                style={{ padding: '8px 24px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'pointer' }}
              >
                İptal
              </button>
              <button
                className="btn-delete"
                onClick={confirmPasswordChange}
                style={{ padding: '8px 24px', borderRadius: '4px', border: 'none', backgroundColor: '#1a1a1a', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Onayla ve Değiştir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;